import React, { useState } from 'react';
import { AlertTriangle, X, FileText, Mail, Loader2 } from 'lucide-react';
import * as api from '../api';
import { toast } from 'sonner';

/**
 * Modale standardisée blocage client.
 * Props :
 *   blocage       — objet { bloque, message, jours_retard, plus_ancienne_echeance, raison }
 *   entrepriseId  — pour les boutons relevé
 *   onClose       — fermer la modale
 */
export function BlocageClientModal({ blocage, entrepriseId, onClose }) {
  const [mailStep, setMailStep]       = useState(false);
  const [mailTo,   setMailTo]         = useState('');
  const [sending,  setSending]        = useState(false);

  if (!blocage) return null;

  const handlePdf = async () => {
    if (!entrepriseId) return;
    try {
      const resp = await api.getEntrepriseRelevePdf(entrepriseId);
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch {
      toast.error('Impossible de générer le relevé PDF');
    }
  };

  const handleMailOpen = async () => {
    if (!entrepriseId) return;
    try {
      const resp = await api.getRelevePdfTemplateMail(entrepriseId);
      const dest = (resp.data?.destinataires || []).join(', ');
      setMailTo(dest);
      setMailStep(true);
    } catch {
      setMailStep(true);
    }
  };

  const handleMailSend = async () => {
    if (!mailTo.trim()) { toast.error('Saisissez au moins un destinataire'); return; }
    setSending(true);
    try {
      const destinataires = mailTo.split(/[,;]/).map(e => e.trim()).filter(Boolean);
      await api.envoyerRelevePdfMail(entrepriseId, {
        destinataires,
        sujet: 'Votre relevé de compte',
        corps: 'Veuillez trouver ci-joint votre relevé de compte.',
      });
      toast.success('Relevé envoyé par mail');
      setMailStep(false);
      onClose?.();
    } catch {
      toast.error("Échec de l'envoi du relevé");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md mx-4 rounded-2xl bg-white shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: '#FEE2E2', background: '#FFF5F5' }}>
          <AlertTriangle size={20} className="text-red-500 flex-shrink-0" />
          <span className="font-semibold text-red-700 flex-1">
            {blocage.raison === 'plafond' ? 'Plafond d\'encours dépassé' : 'Retard de paiement'}
          </span>
          <button onClick={onClose} className="text-red-400 hover:text-red-600 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {!mailStep ? (
            <>
              <p className="text-sm text-gray-700 leading-relaxed">
                {blocage.message || 'Ce client est bloqué. Régularisez la situation avant de continuer.'}
              </p>

              <div className="flex flex-col gap-2 mt-5">
                {entrepriseId && (
                  <>
                    <button
                      data-testid="blocage-btn-releve-pdf"
                      onClick={handlePdf}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl border text-sm font-medium transition-all hover:bg-gray-50"
                      style={{ borderColor: '#E5E7EB', color: '#374151' }}
                    >
                      <FileText size={15} />
                      Relevé de compte (PDF)
                    </button>
                    <button
                      data-testid="blocage-btn-releve-mail"
                      onClick={handleMailOpen}
                      className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl border text-sm font-medium transition-all hover:bg-gray-50"
                      style={{ borderColor: '#E5E7EB', color: '#374151' }}
                    >
                      <Mail size={15} />
                      Relevé de compte par mail
                    </button>
                  </>
                )}
                <button
                  data-testid="blocage-btn-fermer"
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: '#DC2626' }}
                >
                  Fermer
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-3">Destinataire(s) — séparés par virgule ou point-virgule :</p>
              <input
                type="text"
                value={mailTo}
                onChange={e => setMailTo(e.target.value)}
                placeholder="email@exemple.fr, email2@exemple.fr"
                className="w-full px-3 py-2 rounded-lg border text-sm"
                style={{ borderColor: '#D1D5DB' }}
                autoFocus
              />
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setMailStep(false)}
                  className="flex-1 px-4 py-2 rounded-xl border text-sm font-medium transition-all hover:bg-gray-50"
                  style={{ borderColor: '#E5E7EB', color: '#6B7280' }}
                >
                  Retour
                </button>
                <button
                  onClick={handleMailSend}
                  disabled={sending}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
                  style={{ background: '#2563EB' }}
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Envoyer
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
